import React, { useEffect, useMemo } from "react";
import { initMercadoPago, CardPayment } from "@mercadopago/sdk-react";
import { useTranslation } from "react-i18next";
import { Alert, Spinner } from "reactstrap";
import config from "../../config";

type CardPaymentFormData = {
  token?: string;
};

type Props = {
  /** Valor em reais (Brick exige number, não centavos). */
  amountReais: number;
  disabled?: boolean;
  onToken: (cardTokenId: string) => Promise<void>;
  onError?: (message: string) => void;
};

/**
 * Card Payment Brick — tokeniza cartão no browser (PCI); API só recebe card_token_id.
 * Public Key via REACT_APP_MERCADOPAGO_PUBLIC_KEY; sem key o pai usa Fake tok_fake.
 */
const MercadoPagoCardBrick = ({
  amountReais,
  disabled = false,
  onToken,
  onError,
}: Props) => {
  const { t, i18n } = useTranslation();
  const publicKey = config.mercadopago.publicKey;

  // init uma vez por mount — SDK MP exige Public Key antes do Brick.
  useEffect(() => {
    if (!publicKey) return;
    initMercadoPago(publicKey, { locale: brickLocale(i18n.language) });
  }, [publicKey, i18n.language]);

  const amount = useMemo(
    () => Math.max(0.01, Math.round(amountReais * 100) / 100),
    [amountReais]
  );

  if (!publicKey) {
    return null;
  }

  if (disabled) {
    return (
      <div className="text-center py-3">
        <Spinner size="sm" className="me-2" />
        {t("settings.subscription.checkout.processing")}
      </div>
    );
  }

  return (
    <div className="mp-card-payment-brick">
      {/* Por quê dois avisos: PCI (quem processa) + razão social no extrato (confiança Carla). */}
      <Alert color="success" className="border small mb-2">
        {t("settings.subscription.checkout.brickNotice")}
      </Alert>
      <Alert color="light" className="border small mb-3">
        {t("settings.subscription.checkout.brickBillingEntity")}
      </Alert>
      <CardPayment
        initialization={{ amount }}
        onSubmit={async (formData: CardPaymentFormData) => {
          const token = formData?.token;
          if (!token) {
            onError?.(t("settings.subscription.checkout.error"));
            throw new Error("missing_card_token");
          }
          await onToken(token);
        }}
        onError={() => {
          onError?.(t("settings.subscription.checkout.error"));
        }}
      />
    </div>
  );
};

function brickLocale(lang: string): "pt-BR" | "en-US" | "es-AR" {
  const base = (lang || "pt-BR").toLowerCase();
  if (base.startsWith("en")) return "en-US";
  if (base.startsWith("es")) return "es-AR";
  return "pt-BR";
}

export default MercadoPagoCardBrick;

/** True quando o front deve renderizar o Brick (Public Key presente). */
export const hasMercadoPagoPublicKey = (): boolean =>
  Boolean(config.mercadopago.publicKey);
